import React, { useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from './BrandLogo';

function renderInline(text) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`b-${idx}`}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={`t-${idx}`}>{part}</React.Fragment>;
  });
}

function parseMarkdownToBlocks(markdown) {
  return String(markdown || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export default function LegalDocumentPage({ title, filePath }) {
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const blocks = useMemo(() => parseMarkdownToBlocks(content), [content]);

  useEffect(() => {
    let mounted = true;
    fetch(filePath)
      .then(async (res) => {
        if (!res.ok) throw new Error(`${title} unavailable`);
        const text = await res.text();
        if (mounted) setContent(text);
      })
      .catch((e) => {
        if (mounted) setError(e.message || 'Unable to load document');
      });
    return () => { mounted = false; };
  }, [filePath, title]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)', padding: '24px' }}>
      <div style={{ maxWidth: 980, margin: '0 auto', background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 20 }}>
        <BrandLogo height={30} showBeta style={{ marginBottom: 18 }} />
        <Link to="/login" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 13, display: 'inline-block', marginBottom: 12 }}>
          ← Back to Sign In
        </Link>
        <h1 style={{ marginTop: 0, color: 'var(--accent)' }}>{title}</h1>
        {error ? (
          <p style={{ color: '#ef4444' }}>{error}</p>
        ) : (
          <div className="markdown-content" style={{ lineHeight: 1.7, fontSize: 14 }}>
            {!content && 'Loading...'}
            {blocks.map((line, idx) => {
              if (line.startsWith('### ')) {
                return <h3 key={idx} style={{ margin: '14px 0 8px' }}>{renderInline(line.replace(/^###\s+/, ''))}</h3>;
              }
              if (line.startsWith('## ')) {
                return <h2 key={idx} style={{ margin: '18px 0 10px' }}>{renderInline(line.replace(/^##\s+/, ''))}</h2>;
              }
              if (line.startsWith('# ')) {
                return <h1 key={idx} style={{ margin: '20px 0 12px', fontSize: 24 }}>{renderInline(line.replace(/^#\s+/, ''))}</h1>;
              }
              if (line.startsWith('- ') || line.startsWith('* ')) {
                return <p key={idx} style={{ margin: '6px 0 6px 14px' }}>• {renderInline(line.replace(/^[-*]\s+/, ''))}</p>;
              }
              return <p key={idx} style={{ margin: '8px 0' }}>{renderInline(line)}</p>;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
